import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, LayoutDashboard, CalendarCheck, Euro, Clock, MessageSquare, RefreshCw,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, euro } from "@/lib/api";

const STATUS = {
  pending_payment: { label: "En attente paiement", cls: "bg-yellow-500/15 text-yellow-400" },
  confirmed: { label: "Confirmée", cls: "bg-emerald-500/15 text-emerald-400" },
  completed: { label: "Terminée", cls: "bg-blue-500/15 text-blue-400" },
  cancelled: { label: "Annulée", cls: "bg-red-500/15 text-red-400" },
};

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [tab, setTab] = useState("bookings");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, b, c, q] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/admin/contacts"),
        api.get("/admin/quotes"),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setContacts(c.data);
      setQuotes(q.data);
    } catch (e) {
      toast.error("Erreur de chargement.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Statut mis à jour.");
    } catch {
      toast.error("Échec de la mise à jour.");
    }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const cards = [
    { label: "Réservations", value: stats?.total_bookings ?? 0, icon: CalendarCheck },
    { label: "Payées", value: stats?.paid_bookings ?? 0, icon: Clock },
    { label: "Chiffre d'affaires", value: euro(stats?.revenue ?? 0), icon: Euro },
    { label: "Messages", value: stats?.contacts ?? 0, icon: MessageSquare },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink grain">
      <header className="sticky top-0 z-20 bg-ink/80 backdrop-blur-xl border-b border-white/10">
        <div className="mx-auto max-w-7xl px-5 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LayoutDashboard size={18} className="text-gold" />
            <span className="font-serif text-lg text-white">Golden <span className="gold-text">Cab</span> · Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs text-white/40">{admin?.email}</span>
            <button onClick={load} data-testid="refresh-btn" className="text-white/60 hover:text-gold transition-colors"><RefreshCw size={17} /></button>
            <button onClick={doLogout} data-testid="logout-btn" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white/80 hover:border-gold/50 transition-colors">
              <LogOut size={15} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10 py-10">
        <h1 className="font-serif text-3xl text-white mb-8">Tableau de bord</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {cards.map((c) => (
            <div key={c.label} className="rounded-xl border border-white/10 bg-panel p-6">
              <c.icon size={20} className="text-gold" />
              <p className="mt-4 font-serif text-2xl text-white">{c.value}</p>
              <p className="text-xs text-white/45 mt-1">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-6">
          {[["bookings", "Réservations"], ["quotes", "Devis"], ["contacts", "Messages"]].map(([k, l]) => (
            <button
              key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
              className={`rounded-full px-5 py-2 text-sm transition-colors ${tab === k ? "bg-gold text-black" : "border border-white/15 text-white/70 hover:text-white"}`}
            >
              {l}
            </button>
          ))}
        </div>

        {loading && <p className="text-white/40">Chargement…</p>}

        {!loading && tab === "bookings" && (
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-sm" data-testid="bookings-table">
              <thead className="bg-panel text-white/50 text-left text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3">Réf.</th>
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3">Trajet</th>
                  <th className="px-4 py-3">Quand</th>
                  <th className="px-4 py-3">Véhicule</th>
                  <th className="px-4 py-3">Montant</th>
                  <th className="px-4 py-3">Paiement</th>
                  <th className="px-4 py-3">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {bookings.map((b) => (
                  <tr key={b.id} className="hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-gold font-mono text-xs">{b.reference}</td>
                    <td className="px-4 py-3 text-white/85">
                      {b.customer?.civility} {b.customer?.first_name} {b.customer?.last_name}
                      <div className="text-white/40 text-xs">{b.customer?.phone}</div>
                    </td>
                    <td className="px-4 py-3 text-white/70 max-w-[200px]">
                      <div className="truncate">{b.pickup}</div>
                      {b.trip_type !== "hourly" && <div className="text-white/40 text-xs truncate">→ {b.dropoff}</div>}
                    </td>
                    <td className="px-4 py-3 text-white/70 whitespace-nowrap">{b.date} <span className="text-white/40">{b.time}</span></td>
                    <td className="px-4 py-3 text-white/70">{b.vehicle_name}</td>
                    <td className="px-4 py-3 text-white/85">{euro(b.amount)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs ${b.payment_status === "paid" ? "text-emerald-400" : "text-yellow-400"}`}>
                        {b.payment_status === "paid" ? "Payé" : "En attente"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                        <SelectTrigger className="h-8 w-[150px] bg-ink border-white/15 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
                {bookings.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-white/40">Aucune réservation pour le moment.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && tab === "contacts" && (
          <div className="grid md:grid-cols-2 gap-4" data-testid="contacts-list">
            {contacts.map((c) => (
              <div key={c.id} className="rounded-xl border border-white/10 bg-panel p-6">
                <div className="flex items-center justify-between">
                  <p className="text-white">{c.name}</p>
                  <span className="text-xs text-white/40">{(c.created_at || "").slice(0, 10)}</span>
                </div>
                <p className="text-xs text-gold mt-1">{c.email} · {c.phone}</p>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">{c.message}</p>
              </div>
            ))}
            {contacts.length === 0 && <p className="text-white/40">Aucun message.</p>}
          </div>
        )}

        {!loading && tab === "quotes" && (
          <div className="grid md:grid-cols-2 gap-4" data-testid="quotes-list">
            {quotes.map((q) => (
              <div key={q.id} className="rounded-xl border border-white/10 bg-panel p-6">
                <div className="flex items-center justify-between">
                  <p className="text-white">{q.name}</p>
                  <span className="text-xs text-white/40">{(q.created_at || "").slice(0, 10)}</span>
                </div>
                <p className="text-xs text-gold mt-1">{q.email} · {q.phone}</p>
                <p className="mt-3 text-sm text-white/80">{q.pickup} <span className="text-gold">→</span> {q.dropoff}</p>
                <p className="text-xs text-white/45 mt-1">{q.date || "Date à définir"} · {q.passengers} passagers</p>
                {q.message && <p className="mt-2 text-sm text-white/60 leading-relaxed">{q.message}</p>}
              </div>
            ))}
            {quotes.length === 0 && <p className="text-white/40">Aucune demande de devis.</p>}
          </div>
        )}
      </main>
    </div>
  );
}
